import { useState, useRef, useEffect } from 'react';
import { BookOpen, X, Upload, Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { KIND_MAGAZINE } from '@/lib/pressstrKinds';
import type { NostrEvent } from '@nostrify/nostrify';

interface EditMagazineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  magazine: NostrEvent;
  onSuccess?: () => void;
}

export function EditMagazineDialog({ open, onOpenChange, magazine, onSuccess }: EditMagazineDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { mutate: createEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploadingImage } = useUploadFile();

  const slug = magazine.tags.find(([n]) => n === 'd')?.[1] ?? '';

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [language, setLanguage] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);

  // Populate from existing event when dialog opens
  useEffect(() => {
    if (open) {
      setTitle(magazine.tags.find(([n]) => n === 'title')?.[1] ?? '');
      setSummary(magazine.tags.find(([n]) => n === 'summary')?.[1] ?? '');
      setDescription(magazine.content ?? '');
      setImageUrl(magazine.tags.find(([n]) => n === 'image')?.[1] ?? '');
      setWebsite(magazine.tags.find(([n]) => n === 'url')?.[1] ?? '');
      setLanguage(magazine.tags.find(([n]) => n === 'l')?.[1] ?? '');
      setTags(magazine.tags.filter(([n]) => n === 't').map(([, v]) => v));
      setImageFile(null);
      setImagePreview(null);
      setIsSubmitting(false);
    }
  }, [open, magazine]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file.', variant: 'destructive' });
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setImageFile(cloned);
    setImagePreview(URL.createObjectURL(cloned));
    e.target.value = '';
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (t && !tags.includes(t)) { setTags([...tags, t]); setTagInput(''); }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: 'Missing fields', description: 'Title is required.', variant: 'destructive' });
      return;
    }
    if (!user) return;

    setIsSubmitting(true);
    try {
      let finalImageUrl = imageUrl.trim();
      if (imageFile) {
        const uploadedTags = await uploadFile(imageFile);
        const urlTag = uploadedTags.find(([n]) => n === 'url');
        if (urlTag?.[1]) finalImageUrl = urlTag[1];
      }

      const eventTags: string[][] = [
        ['d', slug],
        ['title', title.trim()],
        ['alt', `Magazine: ${title.trim()}`],
      ];
      if (finalImageUrl) eventTags.push(['image', finalImageUrl]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (website.trim()) eventTags.push(['url', website.trim()]);
      if (language.trim()) eventTags.push(['l', language.trim().toLowerCase()]);
      for (const t of tags) eventTags.push(['t', t]);

      createEvent(
        { kind: KIND_MAGAZINE, content: description.trim(), tags: eventTags },
        {
          onSuccess: () => {
            toast({ title: 'Magazine updated!', description: `"${title}" has been updated.` });
            onSuccess?.();
            onOpenChange(false);
          },
          onError: (err) => {
            toast({ title: 'Failed to update', description: err.message, variant: 'destructive' });
            setIsSubmitting(false);
          },
        }
      );
    } catch (err) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting || isPublishing || isUploadingImage;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Edit Magazine
          </DialogTitle>
          <DialogDescription>
            Update metadata for <strong>{magazine.tags.find(([n]) => n === 'title')?.[1]}</strong>. The slug (<code>{slug}</code>) cannot be changed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Cover Image */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {imagePreview ? (
                <div className="relative w-24 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white"><X className="w-3 h-3" /></button>
                </div>
              ) : imageUrl ? (
                <div className="relative w-24 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={imageUrl} alt="Current cover" className="w-full h-full object-cover" />
                  <button onClick={() => imageInputRef.current?.click()} className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity text-white text-xs">Replace</button>
                </div>
              ) : (
                <button onClick={() => imageInputRef.current?.click()} className="w-24 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
              <div className="flex-1 space-y-2">
                <p className="text-xs text-muted-foreground">Upload a new cover or paste a URL.</p>
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/cover.jpg" className="text-xs" disabled={!!imageFile} />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-mag-title">Title *</Label>
            <Input id="edit-mag-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="edit-mag-summary">Short Summary</Label>
            <Input id="edit-mag-summary" value={summary} onChange={(e) => setSummary(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="edit-mag-desc">Full Description (Markdown)</Label>
            <Textarea id="edit-mag-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="edit-mag-website">Website URL</Label>
            <Input id="edit-mag-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="edit-mag-lang">Language Code</Label>
            <Input id="edit-mag-lang" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en, de, fr..." className="mt-1.5 w-32" maxLength={5} />
          </div>
          <div>
            <Label>Topics / Tags</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="Add topic..." />
              <Button type="button" variant="secondary" onClick={addTag}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(t => (
                  <Badge key={t} variant="secondary" className="pl-2">
                    #{t}
                    <button onClick={() => setTags(tags.filter(x => x !== t))} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSubmit} className="flex-1" disabled={busy || !title.trim()}>
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Check className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
